/**
 * One way to file a support ticket, used by every place that offers to.
 *
 * The point is that the caller supplies almost nothing. A button next to a
 * failed analysis passes the job id and the type of complaint; the diagnostics
 * bundle, the route, the build and the browser come from here, and the plan,
 * the credits and the job's own structured failure are added server-side. So
 * the cheapest possible report — a user clicking "report this" and typing
 * nothing — still arrives with enough to work from.
 *
 * What the caller must not pass: anything the server can look up itself. That
 * is not tidiness, it is that a client-asserted plan or credit balance in a
 * support record is a fact nobody should trust and everybody will.
 */
import { supabase } from '@/integrations/supabase/client';
import { collectDiagnostics } from '@/lib/diagnostics';

export type ProblemType =
  | 'analysis'
  | 'technical'
  | 'billing'
  | 'account'
  | 'feature_request'
  | 'other';

export interface ReportProblemInput {
  type: ProblemType;
  /** What the person typed, if they typed anything. */
  description?: string;
  /** The analysis this is about. Unlocks the whole server-side job context. */
  jobId?: string;
  projectId?: string;
  /** A short machine summary — the error already on screen, usually. */
  errorMessage?: string;
}

export interface ReportProblemResult {
  ok: boolean;
  ticketNumber?: string;
  error?: string;
}

export async function reportProblem(input: ReportProblemInput): Promise<ReportProblemResult> {
  try {
    const diagnostics = collectDiagnostics();

    const { data, error } = await supabase.functions.invoke('create-support-ticket', {
      body: {
        errorType: input.type,
        errorMessage: input.errorMessage || 'User reported a problem',
        customerDescription: input.description || null,
        projectId: input.projectId || null,
        pageUrl: diagnostics.route,
        errorDetails: {
          jobId: input.jobId ?? null,
          ...diagnostics,
        },
      },
    });

    if (error) return { ok: false, error: error.message };
    if (data?.error) return { ok: false, error: String(data.error) };

    return { ok: true, ticketNumber: data?.ticket?.ticketNumber };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Unknown error' };
  }
}
